import { useEffect, useMemo, useState } from 'react';
import { useAppAuth } from '../components/AuthContext.jsx';
import { getCalendar } from '../services/jrOverview.js';
import {
  listJchPendingMilestones,
  listSectionMilestones,
  listJchDoneMilestones,
  confirmMilestone,
  scheduleMeeting,
} from '../services/milestone.js';

// Milníky a pevné body (JCH-Milniky). Left column: milestones the JCH still
// has to confirm — hovory to call within 24 h, osobní setkání (setkání type)
// with no 24 h deadline. Right column: the JCH's fixed 1:1 and gate meetings
// in the next 30 days (informational — not yet derived from the backend).
//
// Backend: GET /milestones/jch → { milestones, count } (JCH pending), and
// GET /milestones/jch/done → { milestones, count } (finished). Confirming the
// call is PATCH /:id/confirm, scheduling the setkání is POST /:id/meeting.

const MILESTONE_TITLE = {
  zkouska: 'Složená zkouška',
  primi_nabor: 'První nábor',
  premiers_provize: 'První vyplacená provize z prodeje',
  tempo_1mil: 'Tempo 1 mil.',
  samostatnost: 'Samostatný makléř',
  '2mil_plus': '2 mil. a víc',
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Milniky() {
  const { user } = useAppAuth();
  const isJr = user?.role === 'JR';
  const [pending, setPending] = useState([]);
  const [done, setDone] = useState([]);
  const [fixed, setFixed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    setActionError('');
    try {
      const [p, d] = await Promise.all([
        isJr ? listSectionMilestones() : listJchPendingMilestones(),
        listJchDoneMilestones(),
      ]);
      const list = p.milestones || [];
      setPending(isJr ? list.filter((m) => !m.jrConfirmed) : list);
      setDone(d.milestones || []);
    } catch (err) {
      setError(err.message || 'Milníky se nepodařilo načíst.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCalendar(30).then(setFixed).catch(() => setFixed([]));
    load();
  }, []);

  const openCount = useMemo(
    () => pending.filter((m) => m.status !== 'done').length,
    [pending]
  );

  const handleConfirm = async (id) => {
    setActionError('');
    try {
      await confirmMilestone(id, isJr ? 'JR' : 'JCH');
      await load();
    } catch (err) {
      setActionError(err.message || 'Hovor se nepodařilo potvrdit.');
    }
  };

  const handleSchedule = async (id) => {
    setActionError('');
    try {
      await scheduleMeeting(id);
      await load();
    } catch (err) {
      setActionError(err.message || 'Termín setkání se nepodařilo domluvit.');
    }
  };

  return (
    <section className="dashboard">
      <div className="dashboard__header">
        <div>
          <h1 className="dashboard__title">{isJr ? 'Milníky' : 'Milníky a pevné body'}</h1>
          <p className="dashboard__lead">
            {isJr
              ? 'Upozornění chodí tobě i JCH e-mailem v den, kdy milník vznikne v datech. Volá každý zvlášť. Milníky z provizí vznikají při nahrání provizí.'
              : 'U milníku zavoláš do 24 hodin. U samostatnosti (1,1 mil.) a 2 mil. je místo hovoru osobní setkání, lhůta 24 h pro něj neplatí.'}
          </p>
        </div>
        <div className="dashboard__status">
          {openCount === 0
            ? 'Vše potvrzeno'
            : `${openCount} ${pluralOpen(openCount)} k potvrzení`}
        </div>
      </div>

      {error && (
        <p className="dashboard__error" role="alert">
          {error}
        </p>
      )}
      {actionError && (
        <p className="dashboard__error" role="alert">
          {actionError}
        </p>
      )}

      {loading ? (
        <p className="dashboard__status">Načítá se…</p>
      ) : (
        <div className="milniks">
          {/* Left: pending to confirm */}
          <section className="milniks__panel" aria-labelledby="m-pending">
            <div className="milniks__head">
              <h2 id="m-pending" className="milniks__title">Milníky k potvrzení</h2>
            </div>
            {pending.length === 0 ? (
              <p className="milniks__empty">Žádné milníky k potvrzení.</p>
            ) : (
              <ul className="milniks__list">
                {pending.map((m) => (
                  <li key={m.id} className="milniks__item">
                    <div className="milniks__body">
                      <span className="milniks__name">{m.maklerName || '—'}</span>
                      <span className="milniks__title-line">{milestoneTitle(m)}</span>
                      <span className="milniks__sub">{subtext(m)}</span>
                    </div>
                    <div className="milniks__actions">
                      {m.type === 'setkani' && isJr ? (
                        <span className="milniks__chip">Osobní setkání s JCH · bez lhůty</span>
                      ) : m.type === 'setkani' ? (
                        <button
                          type="button"
                          className="button button--spot"
                          onClick={() => handleSchedule(m.id)}
                          disabled={m.meetingScheduled}
                        >
                          {m.meetingScheduled
                            ? 'Domluveno'
                            : 'Domluvit termín setkání'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="button button--spot"
                          onClick={() => handleConfirm(m.id)}
                          disabled={isJr ? m.jrConfirmed : m.jchConfirmed}
                        >
                          {(isJr ? m.jrConfirmed : m.jchConfirmed)
                            ? 'Hovor potvrzen'
                            : 'Potvrdit hovor'}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <h3 className="milniks__done-title">Výřízené</h3>
            {done.length === 0 ? (
              <p className="milniks__empty">Žádné vyřízené milníky.</p>
            ) : (
              <ul className="milniks__list milniks__list--done">
                {done.map((m) => (
                  <li key={m.id} className="milniks__item">
                    <div className="milniks__body">
                      <span className="milniks__name">{m.maklerName || '—'}</span>
                      <span className="milniks__title-line">{milestoneTitle(m)}</span>
                      <span className="milniks__sub">{subtext(m)}</span>
                    </div>
                    <span className="milniks__chip milniks__chip--done">
                      {m.type === 'setkani' ? 'Setkání' : 'Hovor'} potvrzen
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Right: fixed 1:1 / gate meetings — informational, not yet derived */}
          {isJr ? (
            <section className="milniks__panel" aria-labelledby="m-six">
              <h2 id="m-six" className="milniks__title">Šest milníků makléře</h2>
              <ul className="milniks__list">
                <li className="milniks__item">
                  <div className="milniks__body">
                    <span className="milniks__name">Hovor do 24 h</span>
                    <span className="milniks__sub">složená zkouška · 1. nábor · 1. provize z prodeje · tempo 1 mil.</span>
                  </div>
                </li>
                <li className="milniks__item">
                  <div className="milniks__body">
                    <span className="milniks__name">Osobní setkání s JCH</span>
                    <span className="milniks__sub">samostatnost 1,1 mil. za 12 měsíců · 2 mil. za 12 měsíců – bez lhůty 24 h</span>
                  </div>
                </li>
              </ul>
              <p className="milniks__note">
                Převzatá zakázka se do milníku 1. náboru nepočítá. Při nesložené zkoušce se termíny milníků
                posunou o dobu zpoždění.
              </p>
              <h3 className="milniks__done-title">Kanál upozornění</h3>
              <p className="milniks__note">
                V první verzi e-mail tobě i JCH. Společná WhatsApp skupina JR a JCH přibude v dalších fázích.
                Provize nahráváš ručně každou středu, dokud nebude napojení na Maxis.
              </p>
            </section>
          ) : (
          <section className="milniks__panel" aria-labelledby="m-fixed">
            <h2 id="m-fixed" className="milniks__title">Moje pevné body · příštích 30 dní</h2>
            {fixed.length === 0 ? (
              <p className="milniks__empty">V příštích 30 dnech nejsou žádné pevné body.</p>
            ) : (
              <ul className="milniks__list">
                {fixed.map((e) => (
                  <li key={e.id} className="milniks__item">
                    <div className="milniks__body">
                      <span className="milniks__name">{e.makler}</span>
                      <span className="milniks__title-line">{e.title}</span>
                      <span className="milniks__sub">{fmtDate(e.date)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
          )}
        </div>
      )}

      <p className="milniks__note">
        Milníky: zkouška, 1. nábor, 1. provize z prodeje, tempo 1 mil., samostatnost
        1,1 mil. a 2 mil. Volá i JR. Hovor potvrzuje v Drillu JR i JCH zvlášť.
      </p>
    </section>
  );
}

function milestoneTitle(m) {
  return MILESTONE_TITLE[m.milestoneType] || m.title || '—';
}

function subtext(m) {
  if (m.source) return `Vzniklo ${fmtDate(m.createdAt)}`;
  return '';
}

function pluralOpen(n) {
  if (n === 1) return 'milník';
  const last = n % 10;
  const ten = n % 100;
  if (ten >= 11 && ten <= 19) return 'milníků';
  if (last >= 2 && last <= 4) return 'milníky';
  return 'milník';
}
